import { useEffect, useState } from 'react';
import seedRecords from '../season/match-records.json';
import type { Fixture, VerifiedMatchRecord } from './types';

const seeds = seedRecords as Record<string, VerifiedMatchRecord>;

function validRecord(value: unknown, matchId: string): value is VerifiedMatchRecord {
  if (!value || typeof value !== 'object') return false;
  const record = value as Partial<VerifiedMatchRecord>;
  return record.matchId === matchId && record.productScope === 'arsenal-men-first-team' &&
    typeof record.kickoffUtc === 'string' && Number.isFinite(Date.parse(record.kickoffUtc)) &&
    typeof record.comp === 'string' && record.comp.length > 0 && typeof record.opponent === 'string' && record.opponent.length > 0 &&
    typeof record.opponentShort === 'string' && record.opponentShort.length > 0 && ['H', 'A', 'N'].includes(String(record.venue)) &&
    (record.venue === 'N' ? ['home', 'away'].includes(String(record.arsenalSide)) : record.arsenalSide == null) &&
    Boolean(record.score && Number.isInteger(record.score.home) && Number.isInteger(record.score.away)) &&
    Array.isArray(record.events) && typeof record.summary === 'string' && typeof record.source === 'string' &&
    typeof record.sourceUrl === 'string' && record.sourceUrl.startsWith('https://') &&
    typeof record.checkedAt === 'string' && Number.isFinite(Date.parse(record.checkedAt)) &&
    typeof record.updatedAt === 'string' && Number.isFinite(Date.parse(record.updatedAt)) &&
    Number.isInteger(record.revision) && Number(record.revision) > 0;
}

export function matchRecordMatchesFixture(record: VerifiedMatchRecord, fixture: Fixture): boolean {
  return record.matchId === fixture.id && record.kickoffUtc === fixture.kickoffUtc && record.comp === fixture.comp &&
    record.opponent === fixture.opponent && record.opponentShort === fixture.opponentShort && record.venue === fixture.venue &&
    (record.arsenalSide ?? null) === (fixture.arsenalSide ?? null) && record.score.home === fixture.score?.home && record.score.away === fixture.score?.away;
}

export type MatchRecordState = {
  matchId: string | null;
  record: VerifiedMatchRecord | null;
  loading: boolean;
  online: boolean;
  stored: boolean;
};

export function useMatchRecord(matchId: string | null): MatchRecordState {
  const [state, setState] = useState<MatchRecordState>({ matchId, record: null, loading: Boolean(matchId), online: false, stored: false });

  useEffect(() => {
    let active = true;
    const fallback = matchId ? seeds[matchId] ?? null : null;
    setState({ matchId, record: null, loading: Boolean(matchId), online: false, stored: false });
    if (!matchId) return () => { active = false; };
    fetch(`/api/matches/${encodeURIComponent(matchId)}/record`, { cache: 'no-cache' })
      .then(async (response) => {
        if ([404, 409, 503].includes(response.status)) return { withheld: true } as const;
        if (!response.ok) throw new Error(`match-record:${response.status}`);
        const value = await response.json() as unknown;
        if (!validRecord(value, matchId)) throw new Error('match-record:invalid');
        return value;
      })
      .then((result) => {
        if (!active) return;
        if ('withheld' in result) setState({ matchId, record: null, loading: false, online: true, stored: false });
        else setState({ matchId, record: result, loading: false, online: true, stored: Boolean((result as VerifiedMatchRecord & { recordOrigin?: string }).recordOrigin === 'stored') });
      })
      .catch(() => { if (active) setState({ matchId, record: fallback, loading: false, online: false, stored: false }); });
    return () => { active = false; };
  }, [matchId]);

  return state.matchId === matchId ? state : { matchId, record: null, loading: Boolean(matchId), online: false, stored: false };
}
